import { MovementTypes, SETTLEMENT_TYPES } from "constants/index";
import { roundToThree, surfaceCalc, toNumber, volumeCalc } from "helpers";
import {
  ArticleLiquidation,
  ArticleLocationLog,
  Container,
  ContainerLiquidation,
  LiquidationAggregation,
  LiquidationSettlementType,
  MovementArticleLiquidation,
  MovementLiquidation,
} from "types";
import { groupBy } from "helpers/groupBy";

type LiquidationContainerLike = Container | ContainerLiquidation | NonNullable<ArticleLocationLog["container"]>;

export const calculateBillablePallets = (
  quantity: number,
  unitsPerPallet: number,
) => {
  if (quantity <= 0 || unitsPerPallet <= 0) return 0;

  const pallets = quantity / unitsPerPallet;
  const fullPallets = Math.floor(pallets);
  const fraction = pallets - fullPallets;

  if (fraction === 0) return fullPallets;

  return fullPallets + (fraction <= 0.4 ? 0.5 : 1);
};

export const liquidationArticleOf = (
  liquidation: LiquidationAggregation,
  article_id: number,
) => {
  return liquidation.liquidation_articles.find(
    (article) => article.article_id === article_id,
  );
};

export const liquidationContainerOf = (
  liquidation: LiquidationAggregation,
  article?: ArticleLiquidation,
  log?: ArticleLocationLog,
) => {
  if (log?.container) return log.container;
  if (log?.container_id != null) {
    const container = liquidation.liquidation_containers.find(
      (container) => container.container_id === log.container_id,
    );
    if (container) return container;
  }
  if (!article) return undefined;

  return (
    article.container ||
    liquidation.liquidation_containers.find(
      (container) => container.container_id === article.container_id,
    )
  );
};

export const liquidationArticleVolumeWeight = (
  article?: ArticleLiquidation,
  container?: LiquidationContainerLike,
) => {
  return toNumber(
    article?.volume_weight ??
      article?.container?.volume_weight ??
      container?.volume_weight ??
      0,
  );
};

export const liquidationArticleVolume = (
  article?: ArticleLiquidation,
  container?: LiquidationContainerLike,
) => {
  const articleVolume = volumeCalc(
    article?.width ?? undefined,
    article?.long ?? undefined,
    article?.height ?? undefined,
  );

  if (articleVolume > 0) return articleVolume;

  const containerVolumeByRange = toNumber(
    container?.m3_max ?? article?.container?.m3_max ?? 0,
  );

  if (containerVolumeByRange > 0) return containerVolumeByRange;

  return 0;
};

export const liquidationArticleSurface = (
  article?: ArticleLiquidation,
  container?: LiquidationContainerLike,
) => {
  const articleSurface = surfaceCalc(
    article?.width ?? undefined,
    article?.long ?? undefined,
  );

  if (articleSurface > 0) return articleSurface;

  return 0;
};

export const liquidationLogValue = (
  liquidation: LiquidationAggregation,
  settlementType: LiquidationSettlementType,
  logsOfDay: ArticleLocationLog[],
  article_id: number,
): number => {
  const article = liquidationArticleOf(liquidation, article_id);
  const articleLogs = logsOfDay.filter((log) => log.article_id === article_id);
  if (articleLogs.length > 1) {
    return articleLogs.reduce((sum, log) =>
      sum + liquidationLogValue(liquidation, settlementType, [log], article_id), 0);
  }
  const log = articleLogs[0];
  const container = liquidationContainerOf(liquidation, article, log);

  switch (settlementType.settlement_type) {
    case SETTLEMENT_TYPES.ABONO:
      return 0;

    case SETTLEMENT_TYPES.PALLETS:
      return articleLogs.length;

    case SETTLEMENT_TYPES.PESO:
      let weight = 0;
      const article_weight = article?.weight;
      if (article_weight) {
        articleLogs.forEach((log) => {
          weight += article_weight * log.quantity;
        });
      }
      return weight;

    case SETTLEMENT_TYPES.PESO_VOLUMETRICO:
      let volume_weight = 0;
      const article_volume_weight =
        log?.volume_weight ??
        liquidationArticleVolumeWeight(article, container);
      if (article_volume_weight) {
        articleLogs.forEach((log) => {
          volume_weight += article_volume_weight * log.quantity;
        });
      }
      return volume_weight;

    case SETTLEMENT_TYPES.VOLUMEN:
      let volumen = 0;
      const articleVolume = liquidationArticleVolume(article, container);
      if (articleVolume) {
        articleLogs.forEach((log) => {
          volumen += articleVolume * log.quantity;
        });
      }
      return volumen;

    case SETTLEMENT_TYPES.SUPERFICIE:
      let superficie = 0;
      const articleSurface = liquidationArticleSurface(article, container);
      if (articleSurface) {
        articleLogs.forEach((log) => {
          superficie += articleSurface * log.quantity;
        });
      }
      return superficie;

    default:
      return 0;
  }
};

export const liquidationVolumenSubtotalTotal = (
  settlementType: LiquidationSettlementType,
  volumenUnitsTotal: number,
) => {
  if (settlementType.settlement_type !== SETTLEMENT_TYPES.VOLUMEN) return 0;
  return volumenUnitsTotal * Number(settlementType.calculation ?? 0);
};

export const liquidationVolumenUnitsTotal = (
  settlementType: LiquidationSettlementType,
  liquidation: LiquidationAggregation,
  logsOfDay: ArticleLocationLog[],
) => {
  if (settlementType.settlement_type !== SETTLEMENT_TYPES.VOLUMEN) return 0;

  return logsOfDay.reduce((acc, log) => {
    const article = liquidationArticleOf(liquidation, log.article_id);
    const container = liquidationContainerOf(liquidation, article, log);
    const volumenUnitario = liquidationArticleVolume(article, container);
    if (!volumenUnitario) return acc;

    return acc + volumenUnitario * log.quantity;
  }, 0);
};

const unitsPerPalletOf = (
  liquidation: LiquidationAggregation,
  article_id: number,
) => {
  const article = liquidationArticleOf(liquidation, article_id);
  return Number(article?.units_per_pallet ?? 0);
};

export const fceOf = (
  liquidation: LiquidationAggregation,
  article_id: number,
) => {
  const article = liquidationArticleOf(liquidation, article_id);
  return Number(article?.fce ?? 1);
};

export const liquidationPalletStorageSubtotal = (
  settlementType: LiquidationSettlementType,
  liquidation: LiquidationAggregation,
  log: ArticleLocationLog,
) => {
  if (settlementType.settlement_type !== SETTLEMENT_TYPES.PALLETS) return 0;

  const unitsPerPallet = unitsPerPalletOf(liquidation, log.article_id);
  const calculation = Number(settlementType.calculation ?? 0);
  const fce = fceOf(liquidation, log.article_id);
  const quantity = Number(log.quantity ?? 0);

  if (quantity <= 0 || unitsPerPallet <= 0 || calculation <= 0) return 0;

  return calculateBillablePallets(quantity, unitsPerPallet) * calculation * fce;
};

export const liquidationPalletsSubtotalTotal = (
  settlementType: LiquidationSettlementType,
  liquidation: LiquidationAggregation,
  logsOfDay: ArticleLocationLog[],
) => {
  if (settlementType.settlement_type !== SETTLEMENT_TYPES.PALLETS) return 0;

  return logsOfDay.reduce(
    (acc, log) =>
      acc + liquidationPalletStorageSubtotal(settlementType, liquidation, log),
    0,
  );
};

export const liquidationPalletsUnitsTotal = (
  settlementType: LiquidationSettlementType,
  liquidation: LiquidationAggregation,
  logsOfDay: ArticleLocationLog[],
) => {
  if (settlementType.settlement_type !== SETTLEMENT_TYPES.PALLETS) return 0;

  return logsOfDay.reduce((acc, log) => {
    const upp = unitsPerPalletOf(liquidation, log.article_id);
    if (upp <= 0) return acc;
    return acc + calculateBillablePallets(Number(log.quantity ?? 0), upp);
  }, 0);
};

export const liquidationStaySubtotalTotal = (
  settlementType: LiquidationSettlementType,
  liquidation: LiquidationAggregation,
  logsOfDay: ArticleLocationLog[],
) => {
  if (settlementType.settlement_type === SETTLEMENT_TYPES.ABONO) return 0;

  if (settlementType.settlement_type === SETTLEMENT_TYPES.PALLETS) {
    return liquidationPalletsSubtotalTotal(
      settlementType,
      liquidation,
      logsOfDay,
    );
  }

  const unitsTotal =
    settlementType.settlement_type === SETTLEMENT_TYPES.VOLUMEN
      ? liquidationVolumenUnitsTotal(settlementType, liquidation, logsOfDay)
      : liquidationStayUnitsTotal(settlementType, liquidation, logsOfDay);

  return Number(unitsTotal ?? 0) * Number(settlementType.calculation ?? 0);
};

export const liquidationStayUnitsTotal = (
  settlementType: LiquidationSettlementType,
  liquidation: LiquidationAggregation,
  logsOfDay: ArticleLocationLog[],
) => {
  switch (settlementType.settlement_type) {
    case SETTLEMENT_TYPES.ABONO:
      return 0;
    case SETTLEMENT_TYPES.PALLETS:
      return liquidation.liquidation_movements
        .map((m) => m.pallets)
        .reduce((a, b) => a + b, 0);
    case SETTLEMENT_TYPES.UNIDADES:
      return logsOfDay.reduce((acc, log) => acc + Number(log.quantity ?? 0), 0);
    case SETTLEMENT_TYPES.PESO:
      let weight = 0;
      logsOfDay.forEach((log) => {
        const article = liquidationArticleOf(liquidation, log.article_id);
        const article_weight = article?.weight;

        if (article_weight) weight += article_weight * log.quantity;
      });
      return weight;
    case SETTLEMENT_TYPES.PESO_VOLUMETRICO:
      let volume_weight = 0;
      logsOfDay.forEach((log) => {
        const article = liquidationArticleOf(liquidation, log.article_id);
        const container = liquidationContainerOf(liquidation, article);
        const article_volume_weight = liquidationArticleVolumeWeight(
          article,
          container,
        );

        if (article_volume_weight)
          volume_weight += article_volume_weight * log.quantity;
      });
      return volume_weight;
    case SETTLEMENT_TYPES.VOLUMEN:
      let volumen = 0;
      logsOfDay.forEach((log) => {
        const article = liquidationArticleOf(liquidation, log.article_id);
        const container = liquidationContainerOf(liquidation, article);
        const articleVolume = liquidationArticleVolume(article, container);
        if (articleVolume) volumen += articleVolume * log.quantity;
      });
      return volumen;
    case SETTLEMENT_TYPES.SUPERFICIE:
      let superficie = 0;
      logsOfDay.forEach((log) => {
        const article = liquidationArticleOf(liquidation, log.article_id);
        const container = liquidationContainerOf(liquidation, article);
        const articleSurface = liquidationArticleSurface(article, container);
        if (articleSurface) superficie += articleSurface * log.quantity;
      });
      return superficie;
  }
};

export const liquidationIncomeDischargeUnitsTotal = (
  settlementType: LiquidationSettlementType,
  row: MovementLiquidation,
  articles: MovementArticleLiquidation[],
  liquidation: LiquidationAggregation,
) => {
  switch (settlementType.settlement_type) {
    case SETTLEMENT_TYPES.ABONO:
      return 0;
    case SETTLEMENT_TYPES.PALLETS:
      return row.pallets;
    case SETTLEMENT_TYPES.UNIDADES:
      return articles.reduce((acc, article) => acc + Number(article.amount ?? 0), 0);
    case SETTLEMENT_TYPES.PESO:
      return row.weight;
    case SETTLEMENT_TYPES.VOLUMEN:
      let volumen = 0;
      articles.forEach((movement_article) => {
        const article = liquidationArticleOf(
          liquidation,
          movement_article.article_id,
        );
        const container = liquidationContainerOf(liquidation, article);
        const articleVolume = liquidationArticleVolume(article, container);
        if (articleVolume) volumen += articleVolume * movement_article.amount;
      });
      return volumen;
    case SETTLEMENT_TYPES.SUPERFICIE:
      let superficie = 0;
      articles.forEach((movement_article) => {
        const article = liquidationArticleOf(
          liquidation,
          movement_article.article_id,
        );
        const container = liquidationContainerOf(liquidation, article);
        const articleSurface = liquidationArticleSurface(article, container);
        if (articleSurface)
          superficie += articleSurface * movement_article.amount;
      });
      return superficie;
    case SETTLEMENT_TYPES.PESO_VOLUMETRICO: {
      let volumeWeight = 0;

      articles.forEach((movement_article) => {
        const article = liquidationArticleOf(
          liquidation,
          movement_article.article_id,
        );

        const container = liquidationContainerOf(liquidation, article);

        const articleVolumeWeight = liquidationArticleVolumeWeight(
          article,
          container,
        );

        if (articleVolumeWeight) {
          volumeWeight += articleVolumeWeight * movement_article.amount;
        }
      });

      return volumeWeight;
    }
  }
};

export const liquidationHeader = (settlementType: string) => {
  switch (settlementType) {
    case SETTLEMENT_TYPES.ABONO:
      return "";
    case SETTLEMENT_TYPES.PALLETS:
      return "Pallet";
    case SETTLEMENT_TYPES.UNIDADES:
      return "Cantidad (Unidades)";
    case SETTLEMENT_TYPES.PESO:
      return "Peso";
    case SETTLEMENT_TYPES.PESO_VOLUMETRICO:
      return "Peso Volumétrico";
    case SETTLEMENT_TYPES.SUPERFICIE:
      return "M2";
    case SETTLEMENT_TYPES.VOLUMEN:
      return "M3";
    default:
      return "";
  }
};

const unitValueHelperText = (settlementType: string) => {
  switch (settlementType) {
    case SETTLEMENT_TYPES.PESO:
      return "Valor elegido";
    case SETTLEMENT_TYPES.PESO_VOLUMETRICO:
      return "Valor elegido";
    case SETTLEMENT_TYPES.SUPERFICIE:
      return "Valor (Cant. * M2)";
    case SETTLEMENT_TYPES.VOLUMEN:
      return "Valor (Cant. * M3)";
    default:
      return "";
  }
};

export const liquidationSettlementHeader = (settlementType: string) => {
  switch (settlementType) {
    case SETTLEMENT_TYPES.ABONO:
      return ["Código", "Descripción", "Cantidad (Unidades)"];
    case SETTLEMENT_TYPES.PALLETS:
      return [
        "Código",
        "Descripción",
        "Cantidad (Unidades)",
        "Unidades por Pallet",
        "Total Pallets",
        "Precio",
        "FCE",
        "Subtotal",
      ];
    case SETTLEMENT_TYPES.UNIDADES:
      return ["Código", "Descripción", "Cantidad (Unidades)", "Precio", "Subtotal"];
    case SETTLEMENT_TYPES.PESO:
      return [
        "Código",
        "Descripción",
        "Cantidad (Unidades)",
        "Peso",
        "Peso Volumétrico",
        "Valor elegido",
        "Valor (Cant. * Valor elegido)",
        "Precio",
        "Subtotal",
      ];
    case SETTLEMENT_TYPES.PESO_VOLUMETRICO:
      return [
        "Código",
        "Descripción",
        "Cantidad (Unidades)",
        "Peso",
        "Peso Volumétrico",
        "Valor elegido",
        "Valor (Cant. * Valor elegido)",
        "Precio",
        "Subtotal",
      ];
    case SETTLEMENT_TYPES.SUPERFICIE:
      return [
        "Código",
        "Descripción",
        "Cantidad (Unidades)",
        "Superficie (M2)",
        "Total Superficie",
        "Precio",
        "Subtotal",
      ];
    case SETTLEMENT_TYPES.VOLUMEN:
      return [
        "Código",
        "Descripción",
        "Cantidad (Unidades)",
        "Volumen (M3)",
        "Total Volumen",
        "Precio",
        "Subtotal",
      ];
    default:
      return [];
  }
};

export const liquidationMovementSettlementHeader = (settlementType: string) => {
  switch (settlementType) {
    case SETTLEMENT_TYPES.ABONO:
      return ["Código", "Descripción", "Cantidad (Unidades)"];
    case SETTLEMENT_TYPES.PALLETS:
      return [
        "Código",
        "Descripción",
        "Cantidad (Unidades)",
        "Unidades por Pallet",
        "Total Pallets",
        "Precio",
        "Subtotal",
      ];
    case SETTLEMENT_TYPES.UNIDADES:
      return ["Código", "Descripción", "Cantidad (Unidades)", "Precio", "Subtotal"];
    case SETTLEMENT_TYPES.PESO:
      return [
        "Código",
        "Descripción",
        "Cantidad (Unidades)",
        "Peso",
        "Peso Volumétrico",
        unitValueHelperText(SETTLEMENT_TYPES.PESO),
        "Valor (Cant. * Valor elegido)",
        "Precio",
        "Subtotal",
      ];
    case SETTLEMENT_TYPES.PESO_VOLUMETRICO:
      return [
        "Código",
        "Descripción",
        "Cantidad (Unidades)",
        "Peso",
        "Peso Volumétrico",
        unitValueHelperText(SETTLEMENT_TYPES.PESO_VOLUMETRICO),
        "Valor (Cant. * Valor elegido)",
        "Precio",
        "Subtotal",
      ];
    case SETTLEMENT_TYPES.SUPERFICIE:
      return [
        "Código",
        "Descripción",
        "Cantidad (Unidades)",
        "Superficie (M2)",
        unitValueHelperText(SETTLEMENT_TYPES.SUPERFICIE),
        "Precio",
        "Subtotal",
      ];
    case SETTLEMENT_TYPES.VOLUMEN:
      return [
        "Código",
        "Descripción",
        "Cantidad (Unidades)",
        "Volumen (M3)",
        unitValueHelperText(SETTLEMENT_TYPES.VOLUMEN),
        "Precio",
        "Subtotal",
      ];
    default:
      return [];
  }
};

export const liquidatitonUnitValue = (
  settlementType: string,
  liquidation: LiquidationAggregation,
  movArtLiq?: MovementArticleLiquidation,
  log?: ArticleLocationLog,
) => {
  const articleId = log ? log.article_id : movArtLiq?.article_id;
  const article = articleId
    ? liquidationArticleOf(liquidation, articleId)
    : undefined;
  const container = liquidationContainerOf(liquidation, article, log);

  const unitsPerPallet = article?.units_per_pallet || 1;
  const quantity = log ? log.quantity : movArtLiq?.amount || 0;

  if (settlementType === SETTLEMENT_TYPES.PALLETS) {
    return roundToThree(
      unitsPerPallet > 0 ? Number(quantity ?? 0) / unitsPerPallet : 0,
    );
  }

  switch (settlementType) {
    case SETTLEMENT_TYPES.ABONO:
      return "";
    case SETTLEMENT_TYPES.PESO:
      return roundToThree(log?.physical_weight ?? article?.weight ?? 0);
    case SETTLEMENT_TYPES.UNIDADES:
      return roundToThree(quantity);
    case SETTLEMENT_TYPES.PESO_VOLUMETRICO:
      return roundToThree(log?.volume_weight ?? liquidationArticleVolumeWeight(article, container));
    case SETTLEMENT_TYPES.SUPERFICIE:
      return roundToThree(liquidationArticleSurface(article, container));
    case SETTLEMENT_TYPES.VOLUMEN:
      return roundToThree(liquidationArticleVolume(article, container));
    default:
      return "";
  }
};

export const getAllLiquidations = (liquidation: LiquidationAggregation) => {
  return [liquidation, ...(liquidation.child_liquidations ?? [])];
};

export const buildConsolidatedLiquidation = (
  liquidation: LiquidationAggregation,
): LiquidationAggregation => {
  const allLiquidations = getAllLiquidations(liquidation);

  return {
    ...liquidation,
    liquidation_settlement_types: [
      {
        ...liquidation.liquidation_settlement_types[0],
        subtotal: allLiquidations.reduce(
          (acc, liq) =>
            acc + (liq.liquidation_settlement_types?.[0]?.subtotal ?? 0),
          0,
        ),
      },
      {
        ...liquidation.liquidation_settlement_types[1],
        subtotal: allLiquidations.reduce(
          (acc, liq) =>
            acc + (liq.liquidation_settlement_types?.[1]?.subtotal ?? 0),
          0,
        ),
      },
      {
        ...liquidation.liquidation_settlement_types[2],
        subtotal: allLiquidations.reduce(
          (acc, liq) =>
            acc + (liq.liquidation_settlement_types?.[2]?.subtotal ?? 0),
          0,
        ),
      },
    ],
    subtotal_adicionales: allLiquidations.reduce(
      (acc, liq) => acc + (liq.subtotal_adicionales ?? 0),
      0,
    ),
    total: allLiquidations.reduce((acc, liq) => acc + (liq.total ?? 0), 0),
  };
};

export const isAbonoSettlement = (
  settlementType?: LiquidationSettlementType
): boolean => {
  return settlementType?.settlement_type === SETTLEMENT_TYPES.ABONO;
};

export const getAbonoUnitValue = (
  settlementType: LiquidationSettlementType,
  quantity: number
): number => {
  if (!isAbonoSettlement(settlementType) || !quantity) return 0;
  return settlementType.subtotal / quantity;
};

export const hasSettlementMinimumApplied = (
  liquidation: LiquidationAggregation,
  settlementTypeIndex: number,
) => {
  const settlementType = liquidation.liquidation_settlement_types[settlementTypeIndex];
  const minimumValue = Number(settlementType.minimum_value ?? 0);

  if (minimumValue <= 0) return false;

  if (settlementTypeIndex === 0 || settlementTypeIndex === 2) {
    const movementType =
      settlementTypeIndex === 0 ? MovementTypes.Income : MovementTypes.Discharge;

    return liquidation.liquidation_movements
      .filter((movement) => movement.movement_type_id === movementType)
      .some((movement) => {
        const articles = liquidation.liquidation_movement_articles.filter(
          (ma) => ma.movement_id === movement.movement_id,
        );

        const value = liquidationIncomeDischargeUnitsTotal(
          settlementType,
          movement,
          articles,
          liquidation,
        );

        return minimumValue > Number(value ?? 0);
      });
  }

  const logsGroupedByDay = Object.entries(
    groupBy<ArticleLocationLog>(
      liquidation.liquidation_article_location_log,
      ["date"],
    ),
  ) as [string, ArticleLocationLog[]][];

  return logsGroupedByDay.some(([, logsOfDay]) => {
    const value =
      settlementType.settlement_type === SETTLEMENT_TYPES.PALLETS
        ? liquidationPalletsUnitsTotal(settlementType, liquidation, logsOfDay)
        : settlementType.settlement_type === SETTLEMENT_TYPES.VOLUMEN
          ? liquidationVolumenUnitsTotal(settlementType, liquidation, logsOfDay)
          : liquidationStayUnitsTotal(settlementType, liquidation, logsOfDay);

    return minimumValue > Number(value ?? 0);
  });
};

export const formatLiquidationNo = (
  liquidation: LiquidationAggregation,
  lineIndex?: number,
): string => {
  const baseNo = liquidation.no ?? liquidation.id;

  if (!baseNo) return "-";

  if (!liquidation.parent_liquidation_id) {
    return baseNo.toString();
  }

  return `${baseNo}-${String((lineIndex ?? 0) + 1).padStart(2, "0")}`;
};